import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { LayoutDashboard, Package, ShoppingBag, Users, Mail, IdCard, LogOut, ExternalLink, Workflow, Contact, Bell, Plug, Upload, Sheet } from "lucide-react";

const navItems = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard },
  { to: "/admin/products", end: true, label: "Prodotti", icon: Package },
  { to: "/admin/products/import", label: "Importa Prodotti", icon: Upload },
  { to: "/admin/products/gsheet", label: "Google Sheets", icon: Sheet },
  { to: "/admin/orders", label: "Ordini", icon: ShoppingBag },
  { to: "/admin/crm/pipeline", label: "Pipeline", icon: Workflow },
  { to: "/admin/crm/contacts", label: "Contatti", icon: Contact },
  { to: "/admin/card-requests", label: "Card Club", icon: IdCard },
  { to: "/admin/integrations", label: "Integrazioni", icon: Plug },
  { to: "/admin/admins", label: "Admin", icon: Users },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
];

export default function AdminLayout() {
  const { admin, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [handoffPending, setHandoffPending] = useState(0);
  const [notifBreakdown, setNotifBreakdown] = useState({ handoff: 0, card: 0, orders: 0, new_regs: 0 });
  const [notifOpen, setNotifOpen] = useState(false);
  const lastSeenRef = useRef(0);

  // Poll handoff_pending count every 60s while admin is logged in
  useEffect(() => {
    if (!admin) return;
    let alive = true;
    const beep = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = "sine";
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.18, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
        osc.start();
        osc.stop(ctx.currentTime + 0.6);
      } catch { /* noop */ }
    };
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/crm/stats");
        if (!alive) return;
        const n = data.total_pending || 0;
        if (n > lastSeenRef.current && lastSeenRef.current > 0) {
          beep();
        }
        lastSeenRef.current = n;
        setHandoffPending(n);
        setNotifBreakdown({
          handoff: data.handoff_pending || 0,
          card: data.card_pending || 0,
          orders: data.orders_pending || 0,
          new_regs: data.new_registrations_7d || 0,
        });
      } catch {
        /* silent */
      }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [admin]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="font-heading text-2xl text-yellow-400 uppercase">Caricamento...</div>
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex">
      <aside data-testid="admin-sidebar" className="w-64 bg-zinc-900 border-r-2 border-zinc-800 flex flex-col sticky top-0 h-screen">
        <div className="p-6 border-b-2 border-zinc-800 flex items-start justify-between gap-2">
          <div>
            <div className="font-jp text-yellow-400 text-[10px] tracking-widest">管理パネル</div>
            <div className="font-heading text-2xl text-white uppercase leading-none mt-1">
              DAITA<span className="text-yellow-400">&apos;N&apos;</span>JACK
            </div>
            <div className="text-zinc-500 text-xs uppercase tracking-widest mt-1">Admin</div>
          </div>
          <div className="relative">
            <button
              type="button"
              data-testid="handoff-bell"
              onClick={() => setNotifOpen((v) => !v)}
              aria-label="Notifiche"
              className={`relative p-2 border-2 transition shrink-0 ${
                handoffPending > 0
                  ? "bg-red-500 text-white border-red-500 animate-pulse"
                  : "bg-zinc-950 text-zinc-500 border-zinc-800 hover:text-yellow-400 hover:border-yellow-400"
              }`}
            >
              <Bell className="w-4 h-4" strokeWidth={3} />
              {handoffPending > 0 && (
                <span data-testid="handoff-bell-count" className="absolute -top-2 -right-2 bg-yellow-400 text-black text-[10px] font-heading font-bold px-1.5 py-0.5 border-2 border-black min-w-[20px] text-center">
                  {handoffPending}
                </span>
              )}
            </button>
            {notifOpen && (
              <div data-testid="notif-dropdown" className="absolute right-0 top-full mt-1 w-64 bg-zinc-950 border-2 border-yellow-400 shadow-[6px_6px_0_0_rgba(0,0,0,0.9)] z-50">
                <div className="bg-yellow-400 text-black px-3 py-2 font-heading uppercase tracking-widest text-xs flex items-center justify-between">
                  <span>Notifiche</span>
                  <button onClick={() => setNotifOpen(false)} aria-label="Chiudi" className="bg-black text-yellow-400 px-1.5 py-0.5">×</button>
                </div>
                <ul className="divide-y divide-zinc-800">
                  <li>
                    <Link to="/admin/crm/contacts" onClick={() => setNotifOpen(false)} data-testid="notif-handoff" className="flex items-center justify-between px-3 py-2.5 hover:bg-zinc-900 transition">
                      <span className="text-zinc-200 text-xs font-heading uppercase tracking-widest">Handoff chat</span>
                      <span className={`px-2 py-0.5 text-[10px] font-mono ${notifBreakdown.handoff ? "bg-red-500 text-white" : "bg-zinc-800 text-zinc-500"}`}>{notifBreakdown.handoff}</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/card-requests" onClick={() => setNotifOpen(false)} data-testid="notif-card" className="flex items-center justify-between px-3 py-2.5 hover:bg-zinc-900 transition">
                      <span className="text-zinc-200 text-xs font-heading uppercase tracking-widest">Card Club pending</span>
                      <span className={`px-2 py-0.5 text-[10px] font-mono ${notifBreakdown.card ? "bg-yellow-400 text-black" : "bg-zinc-800 text-zinc-500"}`}>{notifBreakdown.card}</span>
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/orders" onClick={() => setNotifOpen(false)} data-testid="notif-orders" className="flex items-center justify-between px-3 py-2.5 hover:bg-zinc-900 transition">
                      <span className="text-zinc-200 text-xs font-heading uppercase tracking-widest">Ordini nuovi</span>
                      <span className={`px-2 py-0.5 text-[10px] font-mono ${notifBreakdown.orders ? "bg-green-500 text-black" : "bg-zinc-800 text-zinc-500"}`}>{notifBreakdown.orders}</span>
                    </Link>
                  </li>
                  <li className="border-t-2 border-zinc-800">
                    <Link to="/admin/crm/contacts" onClick={() => setNotifOpen(false)} data-testid="notif-new-regs" className="flex items-center justify-between px-3 py-2.5 hover:bg-zinc-900 transition">
                      <span className="text-zinc-200 text-xs font-heading uppercase tracking-widest">Nuove registrazioni · 7gg</span>
                      <span className={`px-2 py-0.5 text-[10px] font-mono ${notifBreakdown.new_regs ? "bg-yellow-400 text-black" : "bg-zinc-800 text-zinc-500"}`}>{notifBreakdown.new_regs}</span>
                    </Link>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 font-heading uppercase tracking-widest text-sm transition border-2 ${
                    isActive
                      ? "bg-yellow-400 text-black border-yellow-400"
                      : "text-zinc-400 border-transparent hover:text-yellow-400 hover:border-yellow-400/40"
                  }`
                }
              >
                <Icon className="w-4 h-4" strokeWidth={3} />
                {it.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="p-4 border-t-2 border-zinc-800 space-y-2">
          <div className="px-2 py-2 text-xs">
            <div className="text-zinc-500 uppercase tracking-widest">Loggato come</div>
            <div className="text-white font-bold truncate">{admin.email}</div>
            <div className="text-yellow-400 text-[10px] uppercase tracking-widest mt-1">{admin.role}</div>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            data-testid="view-site-link"
            className="flex items-center gap-2 px-4 py-2 text-zinc-400 hover:text-yellow-400 text-sm uppercase tracking-widest"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={3} /> Vedi sito
          </a>
          <button
            data-testid="logout-button"
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-4 py-3 bg-red-600/10 border-2 border-red-600/30 text-red-400 hover:bg-red-600 hover:text-white font-heading uppercase tracking-widest text-sm transition"
          >
            <LogOut className="w-4 h-4" strokeWidth={3} /> Logout
          </button>
        </div>
      </aside>

      <main className="flex-1 p-8 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
